import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { AddAttachmentFileModalPage } from '@/components/transactions-page/generateSignaturePacket-page/addAttachmentFileModalPage';
import { EnterBankAccountInfoModalPage } from '@/components/transactions-page/generateSignaturePacket-page/enterBankAccountInfoModalPage';
import { DELETE_BANK_ACCOUNT_MUTATION } from '@/components/transactions-page/generateSignaturePacket-page/enterBankAccountInfoModalPage/queries';
import { EnterContactInfoModalPage } from '@/components/transactions-page/generateSignaturePacket-page/enterContactInfoModalPage';
import { DELETE_CONTACT_MUTATION } from '@/components/transactions-page/generateSignaturePacket-page/enterContactInfoModalPage/queries';
import { RowComponent } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents';
import {
  IDocumentationAttachment,
  IRenderingTemplate,
  IRenderTemplatePage,
  IRenderTemplateRow,
  OpenWizardModalPageArgsType
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/types';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { generateSchemaFromRows } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { GET_LEGAL_ENTITY_DOCUMENTATION_QUERY, UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION } from '../queries';

interface EditSectionFieldsModalPageProps {
  currentEditPage: IRenderTemplatePage;
  templateId: string;
  tenantId: string;
  bankAccounts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  contacts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  bankAccountsAndContactsRefetch: () => void;
  renderingTemplatesRefetch: () => Promise<ApolloQueryResult<{ renderingTemplates: IRenderingTemplate[] }>>;
  handleCloseEditPage: () => void;
}

export const EditSectionFieldsModalPage = ({
  currentEditPage,
  templateId,
  tenantId,
  bankAccounts,
  contacts,
  bankAccountsAndContactsRefetch,
  renderingTemplatesRefetch,
  handleCloseEditPage
}: EditSectionFieldsModalPageProps) => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const entityId = params?.entityId || '';

  const [modalPage, setModalPage] = useState({ isOpen: false, type: '' });
  const [currentRow, setCurrentRow] = useState<IRenderTemplateRow | null>(null);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [currentAttachment, setCurrentAttachment] = useState<IDocumentationAttachment | null>(null);
  const [currentAttachmentSectionInfo, setCurrentAttachmentSectionInfo] = useState({ sectionName: '', expectedAttachmentName: '', contactId: '' });
  const [idsToDelete, setIdsToDelete] = useState<{ type: 'bankAccounts' | 'contacts'; ids: string[] }>({ type: null, ids: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const schema = currentEditPage ? generateSchemaFromRows(currentEditPage.rows as any, true, currentEditPage?.expectedData) : null;
  const methods = useForm<Record<string, any>>({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : null
  });

  const [updateLegalEntityDocumentation] = useMutation(UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION);
  const [deleteBankAccount] = useMutation(DELETE_BANK_ACCOUNT_MUTATION);
  const [deleteContact] = useMutation(DELETE_CONTACT_MUTATION);

  const { data: legalEntityDocumentationData, loading: legalEntityDocumentationLoading } = useQuery(GET_LEGAL_ENTITY_DOCUMENTATION_QUERY, {
    fetchPolicy: 'network-only',
    skip: currentEditPage.name !== 'addAttachments',
    variables: {
      id: entityId
    }
  });

  useEffect(() => {
    if (!currentEditPage.formData) return;
    methods.reset(currentEditPage.formData);
  }, [currentEditPage]);

  const onSubmit = async (data: any) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      // if (idsToDelete.type && idsToDelete.ids.length) {
      //   switch (idsToDelete.type) {
      //     case 'bankAccounts':
      //       for await (const id of idsToDelete.ids) {
      //         await deleteBankAccount({
      //           variables: {
      //             id
      //           }
      //         });
      //       }
      //       break;
      //     case 'contacts':
      //       for await (const id of idsToDelete.ids) {
      //         await deleteContact({
      //           variables: {
      //             id
      //           }
      //         });
      //       }
      //       break;
      //     default:
      //       break;
      //   }
      //   setIdsToDelete({ type: null, ids: [] });
      //   bankAccountsAndContactsRefetch();
      // }

      const dataToUpdate = {
        [currentEditPage.name]: {
          ...data
        }
      };

      await updateLegalEntityDocumentation({
        variables: {
          input: {
            id: entityId,
            templateId,
            kyc: dataToUpdate
          }
        }
      });

      await renderingTemplatesRefetch();
      handleCloseEditPage();
    } catch (error) {
      console.error(error);
      window.scrollTo(0, 0);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModalPage = ({
    type,
    row,
    id = null,
    currentAttachment = null,
    sectionName,
    expectedAttachmentName,
    contactId
  }: OpenWizardModalPageArgsType) => {
    setModalPage({ type, isOpen: true });
    setCurrentRow(row);
    setCurrentItemId(id);
    setCurrentAttachment(currentAttachment);
    setCurrentAttachmentSectionInfo({ sectionName, expectedAttachmentName, contactId });
  };

  const closeModalPage = async () => {
    await methods.trigger(currentRow?.name);
    setModalPage({ type: '', isOpen: false });
    setCurrentRow(null);
    setCurrentItemId(null);
    setCurrentAttachment(null);
  };

  const getFieldValue = (name: string) => {
    return methods.getValues(name);
  };

  const saveFieldValue = (name: string, value: any) => {
    methods.setValue(name, value);
  };

  const unregisterField = (name: string) => {
    methods.unregister(name);
  };

  // Adds an ID to the list of IDs to be deleted. The IDs will be deleted when the save button is clicked.
  const deleteItem = (type: 'bankAccounts' | 'contacts', id: string) => {
    setIdsToDelete({ type, ids: [...idsToDelete.ids, id] });
  };

  if (legalEntityDocumentationLoading) return <LoaderOnWholeScreen />;

  return (
    <>
      {modalPage.isOpen && modalPage.type === 'enter-bank-account-info' && currentRow && (
        <EnterBankAccountInfoModalPage
          row={currentRow}
          currentItemId={currentItemId}
          tenantId={tenantId}
          entityId={entityId ?? ''}
          bankAccounts={bankAccounts}
          mode="documentationTab"
          ignoreRequired
          getBankAccountIds={getFieldValue}
          saveBankAccountIds={saveFieldValue}
          bankAccountsAndContactsRefetch={bankAccountsAndContactsRefetch}
          closeModalPage={closeModalPage}
        />
      )}
      {modalPage.isOpen && modalPage.type === 'enter-contact-info' && currentRow && (
        <EnterContactInfoModalPage
          row={currentRow}
          currentItemId={currentItemId}
          tenantId={tenantId}
          entityId={entityId ?? ''}
          contacts={contacts}
          currentEditPage={currentEditPage}
          ignoreRequired
          getContactIds={getFieldValue}
          saveContactIds={saveFieldValue}
          bankAccountsAndContactsRefetch={bankAccountsAndContactsRefetch}
          closeModalPage={closeModalPage}
        />
      )}
      {modalPage.isOpen && modalPage.type === 'add-an-attachment-file' && currentRow && (
        <AddAttachmentFileModalPage
          row={currentRow}
          entityId={entityId ?? ''}
          contacts={contacts}
          currentAttachment={currentAttachment}
          formData={legalEntityDocumentationData?.getLegalEntityDocumentation || {}}
          currentAttachmentSectionInfo={currentAttachmentSectionInfo}
          getFieldValue={getFieldValue}
          saveFieldValue={saveFieldValue}
          unregisterField={unregisterField}
          closeModalPage={closeModalPage}
        />
      )}
      {!modalPage.isOpen && (
        <>
          {isSubmitting && (
            <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
              <ModalLoader />
            </ModalWrappedContent>
          )}
          <MainWrap>
            <Header modalControl={<GoBackButton handleClose={handleCloseEditPage} backToTitle="" />} />
            <PageTitle title={currentEditPage.title} />
          </MainWrap>
          <MainWrap>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <PaddingWrap>
              <FormProvider {...methods}>
                <form
                  id={`${currentEditPage.name}Form`}
                  onSubmit={methods.handleSubmit(onSubmit, (error) => {
                    console.log(error);
                  })}
                >
                  <EditSectionFieldsWrapper isMobile={isMobile}>
                    {currentEditPage?.rows?.map((row, index) => (
                      <RowComponent
                        key={`${row.name}-${index}`}
                        row={row}
                        mode="documentationTab"
                        contacts={contacts}
                        bankAccounts={bankAccounts}
                        entityId={entityId ?? ''}
                        templateId={templateId}
                        documentationTabFormData={legalEntityDocumentationData?.getLegalEntityDocumentation || {}}
                        ignoreRequired
                        deleteItem={deleteItem}
                        openModalPage={openModalPage}
                      />
                    ))}
                  </EditSectionFieldsWrapper>
                  <FormFooter
                    onCancel={handleCloseEditPage}
                    disableSaveButton={(methods.formState.isSubmitted && !methods.formState.isValid) || isSubmitting}
                    showRequiredFieldsWarning={false}
                  />
                </form>
              </FormProvider>
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const EditSectionFieldsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin-top: 30px;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;
